import type { ThemeFamilyId } from '@evi/skins';

/**
 * Theme -> mini-game mapping for the charging screen.
 *
 * The active theme is read from `useTheme().theme.id` (@evi/skins), which
 * is the EFFECTIVE family — a purchased skin override wins over the
 * driver's picked family, and `theme.id` already reflects that. There is
 * no second source of truth here; this file only maps a family id onto a
 * game bundle.
 *
 * A theme absent from this table has NO game, and the charging screen
 * shows no play affordance at all — not a disabled one.
 *
 * `ns` is the postMessage namespace. Every message in either direction
 * carries it, and anything that doesn't match is ignored: the overlay
 * hosts a same-origin iframe, but a page can receive messages from any
 * frame or extension, so the namespace is the filter that keeps foreign
 * traffic from being read as game state.
 */
/**
 * A game's owning theme family. Live ThemeFamilyId values resolve on
 * the charging screen (the theme's Game Time affordance). 'midnight'
 * and 'sunset' are NOT families: Midnight and Sunset are skin unlocks
 * whose SkinOverride.themeId composes over 'default'. Their games
 * resolve by worn skin code before the family lookup. Plain Default
 * resolves to Blackout Run. Drip, by contrast, IS a family: its id is
 * 'obsidian' and 'Drip' is only the display name, so Overflow resolves live.
 */
export type GameThemeFamily = ThemeFamilyId | 'midnight' | 'sunset';

export interface ThemeGame {
  /** postMessage namespace — `target` outbound, `source` inbound. */
  ns: string;
  /** Static bundle under apps/web/public. Same-origin by construction. */
  src: string;
  /** Display name — a proper noun, never translated. */
  name: string;
  /** Owning theme family (see GameThemeFamily). */
  family: GameThemeFamily;
  /** i18n key stem under the `charging` namespace for the affordance. */
  labelKey: string;
  /** The game's own accent (lifted from its splash palette, not the
   *  theme tokens) — tints its arcade card. */
  accent: string;
  /** i18n key under the `arcade` namespace: one-line description. */
  descriptionKey: string;
  /** i18n key under the `arcade` namespace: the owning theme's name. */
  themeLabelKey: string;
  /**
   * Unlock code of the SKIN this game belongs to, for games tied to a
   * skin rather than a family (Midnight / Sunset compose over
   * 'default', so theme.id alone can never select them). The
   * charging screen tries the worn skin code first, then falls back
   * to the family. Exact catalog codes from
   * packages/db/src/seeds/unlocks.ts.
   */
  skinCode?: string;
}

// Array, not a record: the order IS the arcade card order, arranged to
// space the palette (three accents are green-ish; adjacent they read
// flat). The charging screen looks up by `family` and never cares
// about order.
const GAMES: ThemeGame[] = [
  {
    ns: 'evi-neon-drift',
    src: '/games/neon-drift.html',
    name: 'Power Lap',
    family: 'neon',
    labelKey: 'game.neonDrift',
    accent: '#00ffc3',
    descriptionKey: 'desc.neonDrift',
    themeLabelKey: 'theme.neon',
  },
  {
    ns: 'evi-full-charge',
    src: '/games/full-charge.html',
    name: 'Full Charge',
    family: 'luce',
    labelKey: 'game.fullCharge',
    accent: '#ffd166',
    descriptionKey: 'desc.fullCharge',
    themeLabelKey: 'theme.luce',
  },
  {
    ns: 'evi-blackout-run',
    src: '/games/blackout-run.html',
    name: 'Blackout Run',
    family: 'default',
    labelKey: 'game.blackoutRun',
    accent: '#21c163',
    descriptionKey: 'desc.blackoutRun',
    themeLabelKey: 'theme.default',
  },
  {
    ns: 'evi-overflow',
    src: '/games/overflow.html',
    name: 'Overflow',
    // The Drip theme's family id — 'Drip' is the registry display
    // name for 'obsidian'. Live: Drip-theme charging screens show
    // Game Time for Overflow. The arcade label key stays theme.drip
    // (the DISPLAY name, which is what the card should say).
    family: 'obsidian',
    labelKey: 'game.overflow',
    accent: '#00e0ff',
    descriptionKey: 'desc.overflow',
    themeLabelKey: 'theme.drip',
  },
  {
    // `ns` and `src` deliberately keep the charge-chain name: the namespace is
    // what the game actually listens on and the filename is what it ships as.
    // Only the DISPLAY name was wrong.
    ns: 'evi-charge-chain',
    src: '/games/charge-chain.html',
    name: 'Stay Grounded',
    family: 'cartographic',
    labelKey: 'game.stayGrounded',
    accent: '#5dffb0',
    descriptionKey: 'desc.stayGrounded',
    themeLabelKey: 'theme.cartographic',
  },
  {
    ns: 'evi-volley',
    src: '/games/volley.html',
    name: 'Volley',
    family: 'sunset',
    labelKey: 'game.volley',
    accent: '#ff9a5c',
    descriptionKey: 'desc.volley',
    themeLabelKey: 'theme.sunset',
    skinCode: 'skin_sunset',
  },
  {
    ns: 'evi-current',
    src: '/games/current.html',
    name: 'Current',
    family: 'aurora',
    labelKey: 'game.current',
    accent: '#6effc8',
    descriptionKey: 'desc.current',
    themeLabelKey: 'theme.aurora',
  },
  {
    ns: 'evi-steady-climbing',
    src: '/games/steady-climbing.html',
    name: 'Steady Climbing',
    family: 'midnight',
    labelKey: 'game.steadyClimbing',
    accent: '#12d17e',
    descriptionKey: 'desc.steadyClimbing',
    themeLabelKey: 'theme.midnight',
    skinCode: 'skin_midnight',
  },
  {
    ns: 'evi-surge-pack',
    src: '/games/surge-pack.html',
    name: 'Surge Pack',
    family: 'cyber',
    labelKey: 'game.surgePack',
    accent: '#21c163',
    descriptionKey: 'desc.surgePack',
    themeLabelKey: 'theme.cyber',
  },
];

/** The game for a theme, or null when that theme has none. */
export function gameForTheme(themeId: string | null | undefined): ThemeGame | null {
  if (!themeId) return null;
  return GAMES.find((g) => g.family === themeId) ?? null;
}

/**
 * Skin-aware lookup: the worn skin's game wins, else the effective
 * family's. A Midnight-wearer (theme.id 'default') gets Steady
 * Climbing via skin_midnight; a driver on a plain family gets that
 * family's game; a skin with no game (skin_bloom today) falls back
 * to its base family, so a Default-composed skin without its own
 * game gets Blackout Run. Skin precedence stays unchanged.
 */
export function gameForSkinOrTheme(
  skinCode: string | null | undefined,
  themeId: string | null | undefined,
): ThemeGame | null {
  if (skinCode) {
    const bySkin = GAMES.find((g) => g.skinCode === skinCode);
    if (bySkin) return bySkin;
  }
  return gameForTheme(themeId);
}

/** Every game in arcade card order — the arcade renders from this so a
 *  game added to the table above appears with no arcade change. */
export function allThemeGames(): ThemeGame[] {
  return [...GAMES];
}

/** localStorage key for a game's saved driver profile. Per-namespace so
 *  the two games never read each other's state. */
export function gameProfileKey(ns: string): string {
  return `evi.game.profile.${ns}`;
}
